import { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useMotionValue,
    useSpring,
} from "framer-motion";
import { ArrowDown, Store, MessageCircle } from "lucide-react";
import { waLink } from "../lib/api";

const EASE = [0.16, 1, 0.3, 1];

function Line({ children, index, className = "" }) {
    return (
        <span className={`mask-line ${className}`}>
            <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, ease: EASE, delay: 0.25 + index * 0.13 }}
            >
                {children}
            </motion.span>
        </span>
    );
}

const STATS = [
    { value: "30+", label: "anni di esperienza" },
    { value: "12.000+", label: "ricambi in catalogo" },
    { value: "10+", label: "marchi trattati" },
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

    const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
    const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        ry.set(px * 8);
        rx.set(-py * 6);
    };
    const onLeave = () => {
        rx.set(0);
        ry.set(0);
    };

    return (
        <section
            id="top"
            ref={ref}
            className="relative min-h-screen grid-bg overflow-hidden flex flex-col"
        >
            <div
                className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-primary/15 blur-[140px]"
                style={{ y: glowY }}
            />

            <div className="mx-auto max-w-7xl w-full px-5 sm:px-8 pt-[72px] grid lg:grid-cols-12 gap-10 lg:gap-8 items-center flex-1">
                <div className="lg:col-span-7 pt-16 lg:pt-24 pb-10">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase text-muted-foreground"
                    >
                        CG Service SRL · Scanzano Jonico (MT)
                    </motion.p>

                    <h1 className="mt-6 font-display font-black tracking-tighter leading-[0.92] text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
                        <Line index={0}>
                            Ci prendiamo <span className="text-primary">cura</span>
                        </Line>
                        <Line index={1}>dei tuoi</Line>
                        <Line index={2} className="text-outline">
                            elettrodomestici.
                        </Line>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
                        className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-muted-foreground"
                    >
                        Da trent'anni a Scanzano Jonico: ricambi, consulenza
                        e vendita di elettrodomestici delle migliori marche.
                        Scopri le super offerte del mese, ordina su WhatsApp
                        e paga direttamente in negozio — nessun pagamento online.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 1 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#catalogo"
                            data-testid="hero-cta-catalogo"
                            className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-4 font-display font-bold text-base tracking-tight hover:bg-primary/90 transition-colors duration-200"
                        >
                            <Store className="h-5 w-5" />
                            Scopri le offerte del mese
                            <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-1" />
                        </a>
                        <a
                            href={waLink("Ciao CG Service! Vorrei qualche informazione sui vostri prodotti.")}
                            data-testid="hero-cta-whatsapp"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Scrivici su WhatsApp al 392 485 1819"
                            title="Scrivici su WhatsApp"
                            className="inline-flex items-center gap-2.5 border border-line px-6 py-4 font-mono text-sm tracking-wide text-foreground hover:border-primary hover:text-primary transition-colors duration-200"
                        >
                            <MessageCircle className="h-5 w-5" />
                            392 485 1819
                        </a>
                    </motion.div>

                    <motion.dl
                        initial="hidden"
                        animate="show"
                        variants={{
                            hidden: {},
                            show: { transition: { staggerChildren: 0.12, delayChildren: 1.2 } },
                        }}
                        className="mt-14 grid grid-cols-3 gap-6 border-t border-line pt-8 max-w-xl"
                    >
                        {STATS.map((s) => (
                            <motion.div
                                key={s.label}
                                variants={{
                                    hidden: { opacity: 0, y: 18 },
                                    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
                                }}
                            >
                                <dt className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-foreground">
                                    {s.value}
                                </dt>
                                <dd className="mt-1 font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
                                    {s.label}
                                </dd>
                            </motion.div>
                        ))}
                    </motion.dl>
                </div>

                <div className="lg:col-span-5 relative pb-14 lg:py-24">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.4 }}
                        className="relative mx-auto max-w-[420px] lg:max-w-none"
                        style={{ perspective: 1000 }}
                        onMouseMove={onMove}
                        onMouseLeave={onLeave}
                        data-testid="hero-visual"
                    >
                        <motion.div
                            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
                            className="relative"
                        >
                            <motion.div
                                initial={{ clipPath: "inset(0 0 100% 0)" }}
                                animate={{ clipPath: "inset(0 0 0% 0)" }}
                                transition={{ duration: 1.2, ease: EASE, delay: 0.5 }}
                                className="relative overflow-hidden border border-line"
                            >
                                <motion.img
                                    src="https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=1000&h=1200&q=80"
                                    alt="Lavatrice a carica frontale in mostra da CG Service"
                                    style={{ y: imgY, scale: imgScale }}
                                    className="w-full aspect-[5/6] object-cover"
                                    loading="eager"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                                <div className="absolute bottom-0 inset-x-0 p-5 flex items-center justify-between">
                                    <p className="font-mono text-[10px] tracking-[0.25em] text-white/80 uppercase">
                                        Vendita · Ricambi · Consulenza
                                    </p>
                                    <span className="font-mono text-[10px] tracking-[0.2em] text-primary">
                                        CG-SRV
                                    </span>
                                </div>
                            </motion.div>
                            <div className="absolute -bottom-3 -right-3 h-full w-full border border-primary/40 -z-10" />
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className="pb-8 flex justify-center"
            >
                <a
                    href="#servizi"
                    data-testid="hero-scroll-cue"
                    aria-label="Scorri verso il basso"
                    className="flex flex-col items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scorri</span>
                    <motion.span
                        animate={{ y: [0, 6, 0] }}
                        transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                    >
                        <ArrowDown className="h-4 w-4" />
                    </motion.span>
                </a>
            </motion.div>
        </section>
    );
}